import { useState, useEffect, useCallback, useRef } from 'react';
import { Tabs } from '../components/common/Tabs';
import PageHeader from '../components/common/PageHeader';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { usePollWhile } from '../components/common/useLiveReload';
import { withReturnTo } from '../components/common/returnPath';
import { useDrillStack } from '../components/common/drillLevel';
import { SitePlanChip } from '../components/common/SitePlanChip';
import { planCountsLine, planStateBadge } from '../components/sites/planState';
import SiteReleases from '../components/sites/SiteReleases';
import SiteSearchPanel from '../components/sites/SiteSearchPanel';
import SiteGitHubRepos from '../components/github/SiteGitHubRepos';
import SiteStructurePanel from '../components/sites/SiteStructurePanel';
import { useEscapeKey } from '../hooks/useEscapeKey';
import type {
  ManagedSite,
  ManagedSiteCreate,
  ManagedSiteUpdate,
  ManagedSiteType,
  DeploymentPlan,
  DeploymentPlanCreate,
  DeploymentPlanUpdate,
  WordPressSettings,
  HugoSettings,
  ManualSettings,
  SiteSettings,
  PublishSettings,
  CitationStyle,
  DeploymentValidationResponse,
} from '../types';
import { CITATION_STYLES } from '../components/editor/publishedBody';
import { LoadingRegion } from '../components/common/icons';
import { resolveDeliverTab, tabsFor } from './deliverTabs';
import { isSiteFormDirty, settingsForType, siteFormFromSite } from './deliverSiteForm';
import type { DeliverTab } from './deliverTabs';

const SITE_TYPES: { value: ManagedSiteType; label: string }[] = [
  { value: 'MANUAL', label: 'Manual' },
  { value: 'WORDPRESS', label: 'WordPress' },
  { value: 'HUGO', label: 'Hugo' },
];

function getDefaultSettingsForType(siteType: ManagedSiteType): SiteSettings {
  switch (siteType) {
    case 'WORDPRESS': return { url: '', username: '', password: '' };
    case 'HUGO': return { hero_image_key: '', hero_alt_key: '' };
    case 'MANUAL': return { notes: '' };
    default: return null;
  }
}

// Which front matter keys this Hugo theme reads for the page's header image.
// There's no standard — `image`, `featured_image`, `cover`, `banner` are all in
// the wild — so we ask rather than guess, and emit nothing when it's blank. The
// deployer writes these keys into the front matter itself; the writer never has
// to remember to, so it can't get them wrong.
/**
 * Where a published page's SOURCES come from.
 *
 * A property of the TARGET, not of the page: what a target can render is a fact
 * about the target (WordPress and Hugo take different markdown paths), and one
 * account's docs subdomain and their client's blog can legitimately want
 * different answers. The page builder previews any of these live; this is the
 * one that decides what actually deploys.
 *
 * Options come from `publishedBody.ts` so this screen and that preview cannot
 * drift into describing the same three choices differently.
 */
function CitationStyleField({
  settings,
  onChange,
}: {
  settings: PublishSettings | null;
  onChange: (next: PublishSettings) => void;
}) {
  const s: PublishSettings = settings ?? {};
  const current = s.citation_style || 'off';
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Sources on published pages
      </label>
      <select
        value={current}
        onChange={(e) =>
          onChange({ ...s, citation_style: e.target.value as CitationStyle })
        }
        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
      >
        {CITATION_STYLES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
        {CITATION_STYLES.find((o) => o.value === current)?.hint}{' '}
        Only pages built with tracked citations are affected.
      </p>
    </div>
  );
}

function HeroKeyFields({
  settings,
  onChange,
}: {
  settings: HugoSettings | null;
  onChange: (next: HugoSettings) => void;
}) {
  const s: HugoSettings = settings ?? {};
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Header image front matter key
        </label>
        <input
          type="text"
          value={s.hero_image_key || ''}
          onChange={(e) => onChange({ ...s, hero_image_key: e.target.value })}
          placeholder="e.g. image, featured_image, cover"
          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
        />
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          Whatever your theme reads for the page header image. Leave blank to skip.
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Header alt-text key <span className="text-gray-400">(optional)</span>
        </label>
        <input
          type="text"
          value={s.hero_alt_key || ''}
          onChange={(e) => onChange({ ...s, hero_alt_key: e.target.value })}
          placeholder="e.g. image_alt"
          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
        />
      </div>
    </>
  );
}

// Navigation types for breadcrumb-based navigation
type ViewLevel = 'sites' | 'site' | 'plan';

interface NavigationState {
  view: ViewLevel;
  siteId: number | null;
  planId: number | null;
}

export default function ManagedSites() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Which level we are on is READ FROM THE URL on every render. It used to be
  // state copied into the URL with `replace`, read only at mount — so browser
  // Back skipped both the site and the plan and left Deliver, and a link that
  // moved the address bar left the screen where it was. Each level is now its
  // own history entry (useDrillStack), so Back and the breadcrumb both step up.
  const drill = useDrillStack();
  const nav: NavigationState = (() => {
    const siteId = parseInt(searchParams.get('site') || '', 10) || null;
    const planId = parseInt(searchParams.get('plan') || '', 10) || null;
    if (siteId && planId) return { view: 'plan', siteId, planId };
    if (siteId) return { view: 'site', siteId, planId: null };
    return { view: 'sites', siteId: null, planId: null };
  })();
  const navUrl = (siteId: number | null, planId: number | null) => {
    const params = new URLSearchParams(searchParams);
    if (siteId) params.set('site', String(siteId));
    else params.delete('site');
    if (planId) params.set('plan', String(planId));
    else params.delete('plan');
    // A pane is meaningless without a site, and leaving it behind means the
    // NEXT site you open lands on whatever pane you left the last one on.
    if (!siteId) params.delete('tab');
    const qs = params.toString();
    return `${location.pathname}${qs ? `?${qs}` : ''}`;
  };

  // Sites list
  const [sites, setSites] = useState<ManagedSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Selected site (derived from nav.siteId)
  const [selectedSite, setSelectedSite] = useState<ManagedSite | null>(null);

  // Which pane of the site we are on. Derived from the URL rather than held in
  // state, so a reload, a bookmark and the back button all agree — and so the
  // nav's "pick up where I left off" restores the pane, not just the site.
  //
  // `resolveDeliverTab` needs the site type because one pane is Hugo-only, and a
  // bookmark saved before somebody changed the type must fall back rather than
  // render a pane that has no tab to click out of.
  const tab: DeliverTab = resolveDeliverTab(
    searchParams,
    selectedSite?.site_type ?? null,
  );
  const setTab = (next: DeliverTab) => {
    // MERGE, never replace: `site` and `plan` live in this same query string and
    // dropping them would bounce us out of the site we are looking at.
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    // Panes are siblings: replace, but keep the entry's depth so Back still pops.
    setSearchParams(params, { replace: true, state: drill.state });
  };

  // Site create modal (for new sites only)
  const [showCreateSiteModal, setShowCreateSiteModal] = useState(false);
  const [createSiteForm, setCreateSiteForm] = useState<Partial<ManagedSiteCreate>>({});
  const [createSiteSettings, setCreateSiteSettings] = useState<SiteSettings>(null);
  const [createSiteError, setCreateSiteError] = useState('');
  const [creatingSite, setCreatingSite] = useState(false);

  // Site editing in slideout
  const [siteEditForm, setSiteEditForm] = useState<Partial<ManagedSiteUpdate>>({});
  const [siteEditSettings, setSiteEditSettings] = useState<SiteSettings>(null);
  const [siteEditError, setSiteEditError] = useState('');
  const [savingSiteEdit, setSavingSiteEdit] = useState(false);
  // Says so out loud for a moment. The bar returning to "No changes yet" is
  // the real confirmation, but it is a change of absence — easy to miss when
  // you are looking at the field you just edited rather than at the bar.
  const [justSavedSite, setJustSavedSite] = useState(false);
  // Per-type settings drafts, so flipping the Site Type dropdown to look at
  // another option does not throw away the one you already filled in.
  const [siteTypeDrafts, setSiteTypeDrafts] = useState<
    Partial<Record<ManagedSiteType, SiteSettings>>
  >({});

  // SEEDED FROM THE SAVED SITE, not entered into.
  //
  // There is no edit mode any more, so nothing "opens" the form — it re-seeds
  // when the site it is showing changes. Keyed on `updated_at` as well as `id`
  // so a save re-seeds (clearing the dirty flag) while an unrelated re-render
  // does not throw away what is being typed.
  const seededFrom = useRef<string>('');
  useEffect(() => {
    if (!selectedSite) return;
    const key = `${selectedSite.id}:${selectedSite.updated_at}`;
    if (seededFrom.current === key) return;
    seededFrom.current = key;
    const { form, settings } = siteFormFromSite(selectedSite);
    setSiteEditForm(form);
    setSiteEditSettings(settings);
    setSiteTypeDrafts({});
    setSiteEditError('');
  }, [selectedSite]);

  const siteDirty = selectedSite
    ? isSiteFormDirty(selectedSite, siteEditForm, siteEditSettings)
    : false;

  // ONE FORM, TWO PANES. Site Type and its credentials live on Target; the
  // identity fields on Settings. They share `siteEditForm`, so the bar renders
  // on both — edit on either pane and the other one already knows.
  const requestDiscardSiteEdit = () => {
    if (!selectedSite) return;
    if (!window.confirm('Discard your changes to this site?')) return;
    const { form, settings } = siteFormFromSite(selectedSite);
    setSiteEditForm(form);
    setSiteEditSettings(settings);
    setSiteTypeDrafts({});
    setSiteEditError('');
  };


  // Copy/regenerate key

  // Deployment plans for selected site
  const [plans, setPlans] = useState<DeploymentPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [plansPage, setPlansPage] = useState(1);
  const [plansTotalPages, setPlansTotalPages] = useState(1);
  const [plansTotalItems, setPlansTotalItems] = useState(0);

  // Selected plan for editing
  const [selectedPlan, setSelectedPlan] = useState<DeploymentPlan | null>(null);
  const [planEditForm, setPlanEditForm] = useState<DeploymentPlanUpdate>({});
  const [savingPlan, setSavingPlan] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(false);
  const [planEditError, setPlanEditError] = useState('');

  // Create plan modal
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false);
  const [createPlanForm, setCreatePlanForm] = useState<Partial<DeploymentPlanCreate>>({});
  const [createPlanError, setCreatePlanError] = useState('');
  const [creatingPlan, setCreatingPlan] = useState(false);

  // How many pages this plan holds — shown on the summary card that replaced
  // the embedded page table.
  const [planPagesTotalItems, setPlanPagesTotalItems] = useState(0);

  // Deploy actions
  const [deploying, setDeploying] = useState(false);
  const [deployValidation, setDeployValidation] = useState<DeploymentValidationResponse | null>(null);
  const [showDeployValidation, setShowDeployValidation] = useState(false);
  const [validating, setValidating] = useState(false);


  const fetchSites = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getManagedSites();
      setSites(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sites');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    if (!selectedSite) {
      setPlans([]);
      return;
    }
    setLoadingPlans(true);
    try {
      const data = await api.getDeploymentPlans({
        managedSiteId: selectedSite.id,
        page: plansPage,
        size: 25,
      });
      setPlans(data.items);
      setPlansTotalPages(data.pages);
      setPlansTotalItems(data.total);
    } catch (err) {
      console.error('Failed to load plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  }, [selectedSite, plansPage]);

  // The plan's page COUNT, for the summary card. The full listing (filters,
  // infinite scroll, selection) lives on the Pages workspace now, so this only
  // needs `total` — size:1 keeps it to a single cheap row.
  const fetchPlanPageCount = useCallback(async () => {
    if (!selectedPlan) {
      setPlanPagesTotalItems(0);
      return;
    }
    try {
      const data = await api.getManagedPages({
        deploymentPlanId: selectedPlan.id,
        page: 1,
        size: 1,
      });
      setPlanPagesTotalItems(data.total);
    } catch (err) {
      console.error('Failed to load plan page count:', err);
    }
  }, [selectedPlan]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    fetchPlanPageCount();
  }, [fetchPlanPageCount]);

  // Load the selected site and plan from the URL's ids — on arrival, and again
  // whenever Back or a breadcrumb moves them.
  useEffect(() => {
    if (!nav.siteId) {
      setSelectedSite(null);
      return;
    }
    if (selectedSite?.id === nav.siteId || loading) return;
    const site = sites.find(s => s.id === nav.siteId);
    if (site) {
      setSelectedSite(site);
      setPlansPage(1);
      setSiteEditError('');
    }
  }, [nav.siteId, loading, sites, selectedSite]);

  useEffect(() => {
    if (!nav.planId) {
      setSelectedPlan(null);
      return;
    }
    if (selectedPlan?.id === nav.planId || !selectedSite || loadingPlans) return;
    const plan = plans.find(p => p.id === nav.planId);
    if (plan) {
      setSelectedPlan(plan);
      setPlanEditForm({
        name: plan.name,
        description: plan.description || '',
        plan: plan.plan || '',
      });
      setPlanEditError('');
    }
  }, [nav.planId, selectedSite, loadingPlans, plans, selectedPlan]);

  // Down pushes a history entry; up pops back to the one it came from.
  const goToSites = () => drill.up(nav.view === 'plan' ? 2 : 1, navUrl(null, null));
  const goToSite = (siteId: number) =>
    nav.view === 'plan' ? drill.up(1, navUrl(siteId, null)) : drill.down(navUrl(siteId, null));
  const goToPlan = (siteId: number, planId: number) => drill.down(navUrl(siteId, planId));

  // Site create handlers
  const openSiteCreate = () => {
    setCreateSiteForm({ site_type: 'MANUAL' });
    setCreateSiteSettings(getDefaultSettingsForType('MANUAL'));
    setCreateSiteError('');
    setShowCreateSiteModal(true);
  };

  const handleCreateSiteTypeChange = (newType: ManagedSiteType) => {
    setCreateSiteForm({ ...createSiteForm, site_type: newType });
    setCreateSiteSettings(getDefaultSettingsForType(newType));
  };

  const handleCreateSiteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createSiteForm.name || !createSiteForm.registered_name) {
      setCreateSiteError('Name and domain are required');
      return;
    }
    setCreatingSite(true);
    setCreateSiteError('');
    try {
      const payload = { ...createSiteForm, settings: createSiteSettings as unknown as Record<string, unknown> | undefined };
      await api.createManagedSite(payload as ManagedSiteCreate);
      fetchSites();
      setShowCreateSiteModal(false);
    } catch (err) {
      setCreateSiteError(err instanceof Error ? err.message : 'Failed to create site');
    } finally {
      setCreatingSite(false);
    }
  };

  // Looking at another type must not destroy the one you have configured.
  //
  // This used to overwrite `siteEditSettings` with the new type's defaults, so
  // WordPress -> Hugo -> WordPress silently discarded the url, username and
  // password — the only genuinely destructive control on the pane, and it looked
  // like a dropdown. The drafts are kept per type and only the selected slice is
  // submitted.
  const handleEditSiteTypeChange = (newType: ManagedSiteType) => {
    const previous = siteEditForm.site_type as ManagedSiteType | undefined;
    const kept = previous
      ? { ...siteTypeDrafts, [previous]: siteEditSettings }
      : siteTypeDrafts;
    setSiteTypeDrafts(kept);
    setSiteEditForm({ ...siteEditForm, site_type: newType });
    setSiteEditSettings(settingsForType(kept, newType));
  };

  const handleSaveSiteEdit = async () => {
    if (!selectedSite) return;
    if (!siteEditForm.name || !siteEditForm.registered_name) {
      setSiteEditError('Name and domain are required');
      return;
    }
    setSavingSiteEdit(true);
    setSiteEditError('');
    try {
      const payload = { ...siteEditForm, settings: siteEditSettings as unknown as Record<string, unknown> | undefined };
      const updated = await api.updateManagedSite(selectedSite.id, payload as ManagedSiteUpdate);
      setSites(sites.map(s => s.id === updated.id ? updated : s));
      setSelectedSite(updated);
      // RE-SEED HERE, rather than leaving it to the effect that watches
      // `updated_at`. That effect never fired: the endpoint saves through
      // `ManagedSite.filter(...).update(...)`, and Tortoise only applies
      // `auto_now` when it has a model INSTANCE — a queryset update passes the
      // class, so the timestamp never moved. The save worked every time and the
      // bar went on saying "You have unsaved changes", which reads exactly like
      // a button that does nothing.
      const reseeded = siteFormFromSite(updated);
      seededFrom.current = `${updated.id}:${updated.updated_at}`;
      setSiteEditForm(reseeded.form);
      setSiteEditSettings(reseeded.settings);
      setSiteTypeDrafts({});
      setJustSavedSite(true);
      window.setTimeout(() => setJustSavedSite(false), 2500);
    } catch (err) {
      setSiteEditError(err instanceof Error ? err.message : 'Failed to save site');
    } finally {
      setSavingSiteEdit(false);
    }
  };

  //
  // ALWAYS RENDERED, disabled until there is something to save. Appearing only
  // when dirty made the row itself a surprise — you type one character and a
  // pair of buttons materialises where there were none, which raises the
  // question "where did that come from" before you get to "what does it do".
  // Greyed out, it is a promise the whole time you are in the form, and the
  // only thing that changes is whether it is available.
  const siteSaveBar = (
          <div className="mt-5 flex items-center gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
            <span
              className={`text-sm ${
                siteDirty
                  ? 'text-gray-600 dark:text-gray-300'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              {siteDirty ? 'You have unsaved changes' : 'No changes yet'}
            </span>
            <div className="ml-auto flex gap-2">
              <button
                onClick={handleSaveSiteEdit}
                disabled={!siteDirty || savingSiteEdit}
                title={siteDirty ? undefined : 'Nothing has changed yet'}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
              >
                {savingSiteEdit ? 'Saving...' : 'Save changes'}
              </button>
              <button
                onClick={requestDiscardSiteEdit}
                disabled={!siteDirty || savingSiteEdit}
                title={siteDirty ? undefined : 'Nothing has changed yet'}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
              >
                Discard
              </button>
            </div>
          </div>
  );


  const handleSiteDelete = async (site: ManagedSite) => {
    // Names what survives, because the old wording named only the plans — and
    // sites cascade to plans, which cascaded to every page in them. Deleting a
    // site quietly destroyed everything ever published to it. The pages are
    // unlinked now, so this is the whole truth rather than most of it.
    if (
      !window.confirm(
        `Delete site "${site.name}"?\n\nIts deployment plans go with it. Pages ` +
          `are not deleted - they stay in Pages, unassigned. Anything already ` +
          `published stays live on the site itself.`,
      )
    )
      return;
    try {
      await api.deleteManagedSite(site.id);
      setSites(sites.filter(s => s.id !== site.id));
      if (selectedSite?.id === site.id) {
        setSelectedSite(null);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete site');
    }
  };

  // Plan handlers
  const openPlanCreate = () => {
    if (!selectedSite) return;
    setCreatePlanForm({ managed_site_id: selectedSite.id });
    setCreatePlanError('');
    setShowCreatePlanModal(true);
  };

  const handleCreatePlan = async () => {
    if (!createPlanForm.name || !createPlanForm.managed_site_id) {
      setCreatePlanError('Name is required');
      return;
    }
    setCreatingPlan(true);
    setCreatePlanError('');
    try {
      await api.createDeploymentPlan(createPlanForm as DeploymentPlanCreate);
      setShowCreatePlanModal(false);
      setCreatePlanForm({});
      fetchPlans();
    } catch (err) {
      setCreatePlanError(err instanceof Error ? err.message : 'Failed to create plan');
    } finally {
      setCreatingPlan(false);
    }
  };

  const handleSavePlan = async () => {
    if (!selectedPlan) return;
    setSavingPlan(true);
    setPlanEditError('');
    try {
      const updated = await api.updateDeploymentPlan(selectedPlan.id, planEditForm);
      setSelectedPlan(updated);
      fetchPlans();
    } catch (err) {
      setPlanEditError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingPlan(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedPlan || !selectedSite) return;
    // Says the NUMBER, because "pages will be unlinked" reads as housekeeping
    // until you know it is 340 of them. The promise itself is now kept by the
    // API, which nulls the link before deleting — it used to cascade and
    // destroy every page while this dialog said it would not.
    const pages =
      planPagesTotalItems > 0
        ? ` Its ${planPagesTotalItems} page${planPagesTotalItems === 1 ? '' : 's'} will be unlinked, not deleted - you'll find ${planPagesTotalItems === 1 ? 'it' : 'them'} unassigned in Pages.`
        : '';
    if (!window.confirm(`Delete plan "${selectedPlan.name}"?${pages}`)) return;
    setDeletingPlan(true);
    try {
      await api.deleteDeploymentPlan(selectedPlan.id);
      // Navigate back to site view
      goToSite(selectedSite.id);
      fetchPlans();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeletingPlan(false);
    }
  };

  // AND ON THE SITE'S OWN LIST. The poll below only runs while a plan is
  // open, so going back to the site stopped it, and the plan's card said
  // "deploying" after the deploy had finished or failed.
  usePollWhile(
    !selectedPlan && plans.some(p => p.state === 'DEPLOYING'),
    () => {
      plans
        .filter(p => p.state === 'DEPLOYING')
        .forEach(p =>
          api
            .getDeploymentPlan(p.id)
            .then(updated => setPlans(prev => prev.map(x => (x.id === updated.id ? updated : x))))
            .catch(() => undefined),
        );
    },
    5_000,
  );

  // Poll plan state while DEPLOYING
  useEffect(() => {
    if (!selectedPlan || selectedPlan.state !== 'DEPLOYING') return;
    const interval = setInterval(async () => {
      try {
        const updated = await api.getDeploymentPlan(selectedPlan.id);
        setSelectedPlan(updated);
        setPlans(prev => prev.map(p => p.id === updated.id ? updated : p));
        if (updated.state !== 'DEPLOYING') {
          fetchPlanPageCount();
        }
      } catch (err) {
        console.error('Failed to poll plan state:', err);
      }
    }, 3000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.id, selectedPlan?.state]);

  const handleValidateDeploy = async () => {
    if (!selectedPlan) return;
    setValidating(true);
    try {
      const validation = await api.getDeployValidation(selectedPlan.id);
      setDeployValidation(validation);
      setShowDeployValidation(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to validate deployment');
    } finally {
      setValidating(false);
    }
  };

  const handleDeploy = async () => {
    if (!selectedPlan) return;
    setDeploying(true);
    try {
      const result = await api.deployPlan(selectedPlan.id);
      setSelectedPlan(prev => prev ? { ...prev, state: result.state } : prev);
      setShowDeployValidation(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start deployment');
    } finally {
      setDeploying(false);
    }
  };

  // Esc closes the hand-rolled inline popups / editors on this page.
  useEscapeKey(showCreateSiteModal, () => setShowCreateSiteModal(false));
  useEscapeKey(showDeployValidation, () => setShowDeployValidation(false));
  useEscapeKey(showCreatePlanModal, () => setShowCreatePlanModal(false));

  return (
    // Width/centering is owned by the app shell.
    <div>
      {/* ONE way back, not two. There used to be a bordered "← Back to Managed
          Sites" button in the action slot AND a breadcrumb directly under it —
          same condition, same destination, two styles, stacked. The crumb is
          the one that also says where you are, so it is the one that stayed.

          The root crumb says "Deliver", matching the title. It said "Managed
          Sites", which made three names for one place on one screen: the nav
          calls it "Sites & deploys", the heading "Deliver", the trail something
          else again.

          `onClick` rather than `to`: going up pops the history entries the
          drill-down pushed (useDrillStack), where a link would push new ones. */}
      <PageHeader
        title="Deliver"
        subtitle="Where your pages get published - sites, their deployment plans, and what is waiting to ship."
        className="mb-4 sm:mb-6"
        breadcrumb={
          nav.view === 'sites'
            ? undefined
            : [
                { label: 'Deliver', onClick: goToSites },
                ...(selectedSite
                  ? [{
                      label: selectedSite.name,
                      onClick: nav.view !== 'site' ? () => goToSite(selectedSite.id) : undefined,
                      isCurrent: nav.view === 'site',
                    }]
                  : []),
                ...(selectedPlan && nav.view === 'plan'
                  ? [{ label: selectedPlan.name, isCurrent: true }]
                  : []),
              ]
        }
        actions={
          nav.view === 'sites' ? (
            <button
              onClick={openSiteCreate}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              New Site
            </button>
          ) : undefined
        }
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {/* View: Sites List */}
      {nav.view === 'sites' && (
        loading ? (
          <LoadingRegion label="Loading sites…" />
        ) : sites.length === 0 ? (
          <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg py-12 text-center">
            <p className="text-gray-500 dark:text-gray-400">No managed sites yet.</p>
            <button
              onClick={openSiteCreate}
              className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Add the first one
            </button>
          </div>
        ) : (
          /* CARDS, not rows — the same argument the subject list makes.
             A row could only hold what fits on one line, so the six columns it did
             carry were the six that FIT: name, domain, type, key, created, actions.
             The site's description was not among them, and the API key — a thing you
             copy once and then never look at — took a permanent column of its own
             because a table gives every field the same weight whether or not it earns
             it. A card can rank them: name and domain read first, and the description
             finally appears.

             THE API KEY IS NOT HERE, and used to be a column. Nothing in the app reads
             `ManagedSite.key` — no endpoint authenticates by it, no deployer sends it,
             and the regenerate button rotates a credential that grants nothing. It is a
             stub for a site-side callback that was never built. Showing it made the list
             look like it was handing out secrets. It is still on the site detail view
             until we know whether that integration is coming. */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sites.map((site) => (
              /* A div, not a Link: the site view is a query-param view of THIS page,
                 not a route. The heading is a real button so the card is reachable by
                 keyboard, and every control inside stops the click from also opening
                 the site. */
              <div
                key={site.id}
                onClick={() => goToSite(site.id)}
                className="flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition cursor-pointer"
              >
                <div className="flex items-start justify-between gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      goToSite(site.id);
                    }}
                    className="text-left font-medium text-gray-900 dark:text-gray-100 leading-snug line-clamp-2 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    {site.name}
                  </button>
                  <span className="shrink-0 px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                    {SITE_TYPES.find(t => t.value === site.site_type)?.label || site.site_type}
                  </span>
                </div>

                {/* The domain is what people actually recognise a site by, so it reads
                    directly under the name rather than in a column three across. */}
                <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300 truncate">
                  {site.registered_name}
                </p>

                {site.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                    {site.description}
                  </p>
                )}

                {/* `mt-auto` in a flex column, so the footers line up across a row of
                    cards whatever length the descriptions are. */}
                <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {new Date(site.created_at).toLocaleDateString()}
                  </span>
                  <div className="flex items-center gap-1">
                    {/* One click from the list, not three.
                        The designer lived only inside the site detail view, below the
                        repo links and the deployment plans — so the thing someone opens
                        a Hugo site to DO was the last thing they could find. */}
                    {site.site_type === 'HUGO' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/deliver/${site.id}/design`); }}
                        className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        title="Build this site's look, then commit it to the repo"
                      >
                        Design
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSiteDelete(site); }}
                      className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Site Create Modal */}
      {showCreateSiteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreateSiteModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">New Managed Site</h3>
            {createSiteError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded mb-4 text-sm">{createSiteError}</div>
            )}
            <form onSubmit={handleCreateSiteSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                <input
                  type="text"
                  value={createSiteForm.name || ''}
                  onChange={(e) => setCreateSiteForm({ ...createSiteForm, name: e.target.value })}
                  required
                  placeholder="My Site"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Domain</label>
                <input
                  type="text"
                  value={createSiteForm.registered_name || ''}
                  onChange={(e) => setCreateSiteForm({ ...createSiteForm, registered_name: e.target.value })}
                  required
                  placeholder="example.com"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Public site URL
                  <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">optional</span>
                </label>
                <input
                  type="text"
                  value={createSiteForm.public_base_url || ''}
                  onChange={(e) => setCreateSiteForm({ ...createSiteForm, public_base_url: e.target.value })}
                  placeholder="https://example.com"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                {/* Says what it is FOR, not what it is. Nobody fills in a field
                    labelled "public base URL" for its own sake, and a site with
                    this blank works perfectly until the day someone builds a
                    link map and half the links silently fail to appear. */}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Where readers actually reach this site. Needed for your other sites to link here.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Site Type</label>
                <select
                  value={createSiteForm.site_type || 'MANUAL'}
                  onChange={(e) => handleCreateSiteTypeChange(e.target.value as ManagedSiteType)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {SITE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Site-specific settings */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  {createSiteForm.site_type === 'WORDPRESS' && 'WordPress Settings'}
                  {createSiteForm.site_type === 'HUGO' && 'Hugo Settings'}
                  {createSiteForm.site_type === 'MANUAL' && 'Deployment Notes'}
                </h4>
                {createSiteForm.site_type === 'WORDPRESS' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">WordPress URL</label>
                      <input type="url" value={(createSiteSettings as WordPressSettings)?.url || ''} onChange={(e) => setCreateSiteSettings({ ...createSiteSettings as WordPressSettings, url: e.target.value })} placeholder="https://yoursite.com/wp-json" className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
                      <input type="text" value={(createSiteSettings as WordPressSettings)?.username || ''} onChange={(e) => setCreateSiteSettings({ ...createSiteSettings as WordPressSettings, username: e.target.value })} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Application Password</label>
                      <input type="password" value={(createSiteSettings as WordPressSettings)?.password || ''} onChange={(e) => setCreateSiteSettings({ ...createSiteSettings as WordPressSettings, password: e.target.value })} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                  </div>
                )}
                {createSiteForm.site_type === 'HUGO' && (
                  <div className="space-y-3">
                    <HeroKeyFields
                      settings={createSiteSettings as HugoSettings}
                      onChange={(next) => setCreateSiteSettings(next)}
                    />
                  </div>
                )}
                {(createSiteForm.site_type === 'HUGO' ||
                  createSiteForm.site_type === 'WORDPRESS') && (
                  <div className="mt-3">
                    <CitationStyleField
                      settings={createSiteSettings as PublishSettings}
                      onChange={(next) =>
                        setCreateSiteSettings({ ...createSiteSettings, ...next })
                      }
                    />
                  </div>
                )}
                {createSiteForm.site_type === 'MANUAL' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                    <textarea value={(createSiteSettings as ManualSettings)?.notes || ''} onChange={(e) => setCreateSiteSettings({ ...createSiteSettings as ManualSettings, notes: e.target.value })} rows={3} placeholder="Steps to deploy content..." className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Registration Date</label>
                <input
                  type="date"
                  value={createSiteForm.registration_date || ''}
                  onChange={(e) => setCreateSiteForm({ ...createSiteForm, registration_date: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                <textarea
                  value={createSiteForm.description || ''}
                  onChange={(e) => setCreateSiteForm({ ...createSiteForm, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowCreateSiteModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">Cancel</button>
                <button type="submit" disabled={creatingSite} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{creatingSite ? 'Creating...' : 'Create Site'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View: Site Detail */}
      {nav.view === 'site' && selectedSite && (
        <div className="space-y-6">
          {/* Site Header Card */}
          <div className="bg-white dark:bg-gray-800 border rounded-lg p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{selectedSite.name}</h2>
                  <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                    {SITE_TYPES.find(t => t.value === selectedSite.site_type)?.label}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{selectedSite.registered_name}</p>
                {selectedSite.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">{selectedSite.description}</p>
                )}
                {/* A real link, because this is the one field on the site whose
                    value you check by VISITING it — a typo here produces links
                    that look fine and point at a host that does not answer. */}
                {selectedSite.public_base_url && (
                  <a
                    href={selectedSite.public_base_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block text-sm text-blue-600 dark:text-blue-400 hover:underline mt-1"
                  >
                    {selectedSite.public_base_url} ↗
                  </a>
                )}
              </div>
            </div>

            {/* THE PANES.
                Five stacked cards used to do four unrelated jobs — what is
                published, what is queued, how the site is built, how it is
                configured — with Structure, the one that answers "what is this
                site", last and under a card that expands to a 165-line form.
                Reordering only moves the burial.

                ONE CARD, header and tabs and pane together. Split across two
                cards the tab strip sat above a gap and the content landed in a
                separate box below it, so the tabs read as switching something
                else on the page rather than the panel they are attached to.

                `flex-wrap`, not `overflow-x-auto`: scrolling four short labels
                bought nothing and put a scrollbar across the card. */}
            <div className="mt-5 -mx-6 px-6">
              <Tabs
                tabs={tabsFor(selectedSite.site_type)}
                active={tab}
                onChange={setTab}
                className=""
              />
            </div>

            {/* The pane. Same card, under the tabs it belongs to. */}
            <div className="pt-6">
            {/* SETTINGS IS A FORM, NOT A MODE.
                There was an Edit button, and it sat outside the edit/view
                branch — so it stayed live while the form was open and clicking
                it re-ran the seeder, silently discarding what had been typed. A
                mode you can re-enter while already in it has no meaning.

                The read-only grid it toggled with is gone too: it rendered a
                strict subset of these same fields in different markup, which is
                a second copy of one truth and free to drift from it. The API key
                went with it — nothing in the app reads `ManagedSite.key`, so a
                copy button and a regenerate button were offering a credential
                that authorises nothing. */}
            {tab === 'settings' && (
              <div>
              {siteEditError && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded mb-4 text-sm">{siteEditError}</div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                  <input
                    type="text"
                    value={siteEditForm.name || ''}
                    onChange={(e) => setSiteEditForm({ ...siteEditForm, name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Domain</label>
                  <input
                    type="text"
                    value={siteEditForm.registered_name || ''}
                    onChange={(e) => setSiteEditForm({ ...siteEditForm, registered_name: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Public site URL
                    <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">optional</span>
                  </label>
                  <input
                    type="text"
                    value={siteEditForm.public_base_url || ''}
                    onChange={(e) => setSiteEditForm({ ...siteEditForm, public_base_url: e.target.value })}
                    placeholder="https://example.com"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Where readers actually reach this site. Needed for your other sites to link here.
                    Include a path if the site lives in a subdirectory, e.g. <code>https://example.com/blog</code>.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Registration Date</label>
                  <input
                    type="date"
                    value={siteEditForm.registration_date || ''}
                    onChange={(e) => setSiteEditForm({ ...siteEditForm, registration_date: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                  <textarea
                    value={siteEditForm.description || ''}
                    onChange={(e) => setSiteEditForm({ ...siteEditForm, description: e.target.value })}
                    rows={2}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </div>



                {siteSaveBar}
              </div>
            )}

            {/* HOW THIS SITE PUBLISHES — its type, whatever that type needs to
                reach the target, and for a site we generate the repo it commits
                into and the designer.

                The Site Type selector lives HERE, not in Settings, because
                everything it governs is on this pane: change it and the
                credentials below change with it. Settings is what the site IS;
                this is how it ships. */}
            {/* WHAT SEARCH MADE OF IT — Search Console, and the 28/56-day
                queues. Its own pane because it is a different question from
                how the site publishes: not "did it ship" but "did anyone see it". */}
            {tab === 'search' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
                  Search
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Which pages Google shows, and what to do with the ones it doesn't.
                </p>
                <SiteSearchPanel siteId={selectedSite.id} />
              </div>
            )}

            {tab === 'target' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
                  Publishing target
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  How this site publishes - what kind of site it is, and what
                  that takes to reach it.
                </p>
                {siteEditError && (
                  <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded mb-4 text-sm">{siteEditError}</div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Site Type</label>
                  <select
                    value={siteEditForm.site_type || 'MANUAL'}
                    onChange={(e) => handleEditSiteTypeChange(e.target.value as ManagedSiteType)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    {SITE_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  {/* Names the visible consequence, on the pane where all of it
                      happens. The sections below change with the type; the
                      unsaved state does not preview them, because something
                      that shows up while you type and vanishes when you discard
                      is a moving target. */}
                  {siteEditForm.site_type !== selectedSite.site_type && (
                    <p className="mt-1.5 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded px-2 py-1.5">
                      Saving changes how this site publishes. Hugo commits into a
                      linked repository and can be shaped in the designer;
                      WordPress publishes over the REST API; Manual publishes
                      nowhere automatically. The sections below this form change
                      to match once you save.
                    </p>
                  )}
                </div>
              {/* THE REVIEW GATE, on Target because it is a property of how
                  this site publishes rather than of what the site is. Off by
                  default and it must stay that way for existing sites: turning
                  it on globally would block every page already marked ready. */}
              <label className="mt-4 flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!siteEditForm.require_review}
                  onChange={(e) =>
                    setSiteEditForm({ ...siteEditForm, require_review: e.target.checked })
                  }
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    Someone must read a page before it publishes here
                  </span>
                  <span className="block text-gray-500 dark:text-gray-400">
                    Pages marked <em>Ready to deploy</em> wait in the review queue
                    instead of going out. Approval is per version, so a page
                    rebuilt after it was approved comes back for another look.
                  </span>
                </span>
              </label>

              {selectedSite.require_review && (
                <button
                  onClick={() => navigate(`/deliver/review?site=${selectedSite.id}`)}
                  className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Open the review queue for this site →
                </button>
              )}

              {/* Site-specific settings */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  {siteEditForm.site_type === 'WORDPRESS' && 'WordPress Settings'}
                  {siteEditForm.site_type === 'HUGO' && 'Hugo Settings'}
                  {siteEditForm.site_type === 'MANUAL' && 'Deployment Notes'}
                </h4>
                {siteEditForm.site_type === 'WORDPRESS' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">WordPress URL</label>
                      <input type="url" value={(siteEditSettings as WordPressSettings)?.url || ''} onChange={(e) => setSiteEditSettings({ ...siteEditSettings as WordPressSettings, url: e.target.value })} placeholder="https://yoursite.com/wp-json" className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
                      <input type="text" value={(siteEditSettings as WordPressSettings)?.username || ''} onChange={(e) => setSiteEditSettings({ ...siteEditSettings as WordPressSettings, username: e.target.value })} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Application Password</label>
                      <input
                        type="password"
                        value={(siteEditSettings as WordPressSettings)?.password || ''}
                        onChange={(e) => setSiteEditSettings({ ...siteEditSettings as WordPressSettings, password: e.target.value })}
                        onFocus={(e) => { if (e.target.value === '********') setSiteEditSettings({ ...siteEditSettings as WordPressSettings, password: '' }); }}
                        placeholder="Enter new password to change"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                    </div>
                  </div>
                )}
                {siteEditForm.site_type === 'HUGO' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <HeroKeyFields
                      settings={siteEditSettings as HugoSettings}
                      onChange={(next) => setSiteEditSettings(next)}
                    />
                  </div>
                )}
                {(siteEditForm.site_type === 'HUGO' ||
                  siteEditForm.site_type === 'WORDPRESS') && (
                  <div className="mt-4 md:max-w-sm">
                    <CitationStyleField
                      settings={siteEditSettings as PublishSettings}
                      onChange={(next) =>
                        setSiteEditSettings({ ...siteEditSettings, ...next })
                      }
                    />
                  </div>
                )}
                {siteEditForm.site_type === 'MANUAL' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                    <textarea value={(siteEditSettings as ManualSettings)?.notes || ''} onChange={(e) => setSiteEditSettings({ ...siteEditSettings as ManualSettings, notes: e.target.value })} rows={3} placeholder="Steps to deploy content..." className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" />
                  </div>
                )}
              </div>

                {/* ALWAYS RENDERED, disabled until there is something to save.
                    Appearing only when dirty made the row itself a surprise —
                    you type one character and a pair of buttons materialises
                    where there were none, which raises the question "where did
                    that come from" before you get to "what does it do". Greyed
                    out, it is a promise the whole time you are in the form, and
                    the only thing that changes is whether it is available. */}
                <div className="mt-5 flex items-center gap-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <span
                    className={`text-sm ${
                      justSavedSite
                        ? 'text-green-700 dark:text-green-400'
                        : siteDirty
                          ? 'text-gray-600 dark:text-gray-300'
                          : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {justSavedSite
                      ? 'Saved'
                      : siteDirty
                        ? 'You have unsaved changes'
                        : 'No changes yet'}
                  </span>
                  <div className="ml-auto flex gap-2">
                    <button
                      onClick={handleSaveSiteEdit}
                      disabled={!siteDirty || savingSiteEdit}
                      title={siteDirty ? undefined : 'Nothing has changed yet'}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                    >
                      {savingSiteEdit ? 'Saving...' : 'Save changes'}
                    </button>
                    <button
                      onClick={requestDiscardSiteEdit}
                      disabled={!siteDirty || savingSiteEdit}
                      title={siteDirty ? undefined : 'Nothing has changed yet'}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                    >
                      Discard
                    </button>
                  </div>
                </div>

                {/* Hugo only — and the reason the PANE is not. A site we
                    generate has a repo to commit into and a look to shape; a
                    WordPress theme is the customer's, with nothing of ours to
                    render. */}
                {selectedSite.site_type === 'HUGO' && (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <SiteGitHubRepos siteId={selectedSite.id} siteType={selectedSite.site_type} />
                  </div>
                )}
              </div>
            )}

          {/* Site design (Hugo only).
              Deploying pages assumes the site already exists — themed, built and
              hosted. For anyone whose main site is on a platform we can't reach,
              standing that up was the blocker; this is where they do it. */}
            {tab === 'target' && selectedSite.site_type === 'HUGO' && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Site design
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-xl">
                    Build this site's look by talking to the designer - colors, fonts,
                    layout and navigation - then commit the whole Hugo site into the
                    linked repository in one go. Point your subdomain at it and every
                    page you deploy lands inside it.
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/deliver/${selectedSite.id}/design`)}
                  className="px-4 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-md hover:opacity-90 text-sm whitespace-nowrap"
                >
                  Open designer
                </button>
              </div>
            </div>
          )}

            {/* Deployment Plans Section */}
            {tab === 'publishing' && (
            <div>
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Deployment plans ({plansTotalItems})
                </h3>
                {/* SAID OUT LOUD, always, and not behind a tooltip. Nothing in
                    the product has ever explained what a plan is, and the whole
                    concept has to survive a first read or the screen is a list
                    of names. The last sentence is the part people get wrong:
                    a plan is not a batch that empties. */}
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
                  A plan is a permanent list of pages for this site. Pages join
                  one when you make them, and <strong>Deploy</strong> sends every
                  page in it that you have marked <em>Ready to deploy</em>
                  {selectedSite.site_type === 'HUGO'
                    ? ' to the linked GitHub repository'
                    : selectedSite.site_type === 'WORDPRESS'
                      ? ' to this WordPress site over the REST API'
                      : ''}
                  . Published pages stay in the plan, so it is both what is
                  waiting and what you have already shipped.
                </p>
              </div>
              <button
                onClick={openPlanCreate}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm whitespace-nowrap shrink-0"
              >
                New Plan
              </button>
            </div>

            {/* The four steps, each linking where it happens. Folded away
                because it is onboarding: read once, then in the way. */}
            <details className="mb-4 text-sm">
              <summary className="cursor-pointer text-blue-600 dark:text-blue-400 hover:underline">
                How a page gets published
              </summary>
              <ol className="mt-2 ml-5 list-decimal space-y-1 text-gray-600 dark:text-gray-300">
                <li>
                  Give the page a plan - from the page builder, <strong>Make
                  pages</strong>, or <strong>Change plan</strong> in{' '}
                  <button onClick={() => navigate('/pages')} className="text-blue-600 dark:text-blue-400 hover:underline">Pages</button>.
                </li>
                <li>
                  Build it, read it, then hit <strong>Mark ready to deploy</strong> in{' '}
                  <button onClick={() => navigate('/pages')} className="text-blue-600 dark:text-blue-400 hover:underline">Pages</button>.
                  Only pages that would publish cleanly can be marked.
                </li>
                <li>Come back here, open the plan and press <strong>Deploy</strong>.</li>
                <li>
                  Sent pages become live, keep their public URL, and stay in the
                  plan. Deploying again sends only what has newly become ready.
                </li>
              </ol>
            </details>

            {loadingPlans ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading plans...</div>
            ) : plans.length === 0 ? (
              <div className="text-center py-10 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                <p className="text-gray-500 dark:text-gray-400">
                  No plans yet. A plan is where pages line up to go live on this
                  site - most sites need only one.
                </p>
                <button
                  onClick={openPlanCreate}
                  className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Create the first plan
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    onClick={() => goToPlan(selectedSite.id, plan.id)}
                    className="p-4 border rounded-lg cursor-pointer transition-colors border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-700"
                  >
                    <div className="flex items-start gap-2 mb-2">
                      <SitePlanChip
                        siteName={selectedSite?.name || null}
                        planName={plan.name}
                      />
                      <span className="font-medium text-gray-900 dark:text-gray-100 leading-snug">{plan.name}</span>
                      {/* Mapped, never printed raw. `EDITING` is the model
                          default that nothing resets, so it only ever means
                          "never deployed" — the enum name says the opposite. */}
                      {planStateBadge(plan.state) && (
                        <span
                          className={`ml-auto shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded whitespace-nowrap ${planStateBadge(plan.state)!.className}`}
                        >
                          {planStateBadge(plan.state)!.label}
                        </span>
                      )}
                    </div>
                    {plan.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{plan.description}</p>
                    )}
                    {/* WHAT IS IN IT. The created date was here and has never
                        answered a question; "3 ready to deploy" is the reason
                        you opened this screen. */}
                    <p className="text-xs mt-2 text-gray-500 dark:text-gray-400">
                      {planCountsLine(plan) ?? (
                        <span className="text-gray-400 dark:text-gray-500">
                          No pages yet - assign some from Pages.
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {plan.last_deployed_at
                        ? `Last published ${new Date(plan.last_deployed_at).toLocaleDateString()}`
                        : 'Never published'}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* WHAT HAS ACTUALLY SHIPPED. The plans above are what is queued;
                this is the record of every deploy, which the product could not
                show at all — `last_run_details` held only the last run, so each
                deploy erased the one before it. */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
                Deploy history
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Every deploy to this site, newest first. Click one to see the
                pages it sent.
              </p>
              <SiteReleases siteId={selectedSite.id} />
            </div>

            {plansTotalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm border-t border-gray-200 dark:border-gray-700 pt-4">
                <span className="text-gray-500 dark:text-gray-400">Page {plansPage} of {plansTotalPages}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPlansPage(p => Math.max(1, p - 1))}
                    disabled={plansPage === 1}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPlansPage(p => Math.min(plansTotalPages, p + 1))}
                    disabled={plansPage === plansTotalPages}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          )}

          {/* THE LANDING PANE. It used to be last, under everything else, which
              is what the restructure is for: this is the only place in the
              product that shows the site as a whole, and it is the answer to
              "what is this site" that the screen was previously making you
              scroll past four other cards to reach.

              The other half of storing a path on the page rather than deriving
              it: copied paths can drift, and this is where the drift shows. */}
            {tab === 'structure' && (
            <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
              Site structure
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Where this site&rsquo;s pages actually publish, grouped by their
              publish path. Click a page to open it.
            </p>
            <SiteStructurePanel siteId={selectedSite.id} />
            </div>
            )}
            </div>
          </div>
        </div>
      )}

      {/* View: Plan Editor */}
      {nav.view === 'plan' && selectedSite && selectedPlan && (
        <div className="space-y-6">
          {/* Plan Header & Edit Form */}
          <div className="bg-white dark:bg-gray-800 border rounded-lg p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <SitePlanChip
                  siteName={selectedSite.name}
                  planName={selectedPlan.name}
                  size="md"
                />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{selectedPlan.name}</h2>
                {/* The same badge the cards use. This branch printed the enum
                    raw for every state but DEPLOYING, so a plan nobody had ever
                    deployed announced itself as "EDITING". */}
                {planStateBadge(selectedPlan.state) && (
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded ${planStateBadge(selectedPlan.state)!.className}`}
                  >
                    {planStateBadge(selectedPlan.state)!.label}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                {/* Read the site this deploy produces, before running it.
                    The designer's preview compiles the SAME shell that becomes
                    the Hugo layouts and fills it with these pages, so scoping it
                    to the plan turns a design tool into the only answer the app
                    has to "what are we about to publish". Hugo only: on a
                    WordPress site the theme is the customer's and there is
                    nothing of ours to render. */}
                {selectedSite.site_type === 'HUGO' && (
                  <button
                    onClick={() =>
                      navigate(
                        withReturnTo(
                          `/deliver/${selectedSite.id}/design/preview?plan=${selectedPlan.id}&ready_only=1`,
                          `${location.pathname}${location.search}`,
                          'this deploy',
                        ),
                      )
                    }
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                    title="Browse the site as this deploy will leave it"
                  >
                    Preview this deploy
                  </button>
                )}
                {/* NOT Hugo-only. `get_deployer` returns a WordPress deployer
                    for a WordPress site and only raises for MANUAL, and
                    `validate_deploy` is site-type agnostic — a missing or
                    misconfigured deployer comes back as the `error` string the
                    modal renders first. The button was gated for no backend
                    reason, so a WordPress site could hold pages marked ready and
                    offer no way to send them. */}
                {selectedSite.site_type !== 'MANUAL' && (
                  <button
                    onClick={handleValidateDeploy}
                    disabled={validating || deploying || selectedPlan.state === 'DEPLOYING'}
                    className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 text-sm"
                    title={
                      selectedSite.site_type === 'HUGO'
                        ? 'Send every page marked Ready to deploy to the linked GitHub repository'
                        : 'Send every page marked Ready to deploy to this WordPress site over the REST API'
                    }
                  >
                    {validating ? 'Validating...' : selectedPlan.state === 'DEPLOYING' ? 'Deploying...' : 'Deploy'}
                  </button>
                )}
                {/* A silent absence reads as a bug. There is no deployer for a
                    Manual site — that is the definition of the type — so say so
                    where the button would have been. */}
                {selectedSite.site_type === 'MANUAL' && (
                  <p className="self-center text-sm text-gray-500 dark:text-gray-400 max-w-md">
                    Manual site - nothing here can publish for you. Mark pages
                    ready, then take them to the site yourself.
                  </p>
                )}
                {/* Queueing pages for build moved to the Pages workspace, where
                    it acts on a SELECTION. The button that used to sit here
                    queued every page in the plan with no way to narrow it. */}
                <button
                  onClick={() => navigate(`/pages?plan=${selectedPlan.id}&site=${selectedSite.id}`)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                  title="Open these pages in the Pages workspace to filter, select and queue them"
                >
                  View pages →
                </button>
                <button
                  onClick={handleSavePlan}
                  disabled={savingPlan}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                >
                  {savingPlan ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleDeletePlan}
                  disabled={deletingPlan}
                  className="px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 text-sm"
                >
                  {deletingPlan ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>

            {planEditError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded mb-4 text-sm">{planEditError}</div>
            )}

            {/* Last run details */}
            {selectedPlan.last_run_details && (
              <div className={`mb-4 p-3 rounded text-xs font-mono whitespace-pre-wrap ${
                selectedPlan.state === 'ERROR' ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
              }`}>
                {selectedPlan.last_run_details}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                <input
                  type="text"
                  value={planEditForm.name || ''}
                  onChange={(e) => setPlanEditForm({ ...planEditForm, name: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                <input
                  type="text"
                  value={planEditForm.description || ''}
                  onChange={(e) => setPlanEditForm({ ...planEditForm, description: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Plan Notes</label>
                <textarea
                  value={planEditForm.plan || ''}
                  onChange={(e) => setPlanEditForm({ ...planEditForm, plan: e.target.value })}
                  rows={4}
                  placeholder="Add notes about this deployment plan..."
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Pages in Plan — summary only.
              The page table that used to live here (filters, selection, bulk
              move / delete / queue) moved to the top-level Pages workspace.
              Two copies of the same table drift, and operating on a batch of
              pages is application work that shouldn't be reachable only by
              drilling into Settings. This screen keeps site + plan config. */}
          <div className="bg-white dark:bg-gray-800 border rounded-lg p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Pages in Plan
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {planPagesTotalItems.toLocaleString()} page{planPagesTotalItems !== 1 ? 's' : ''} assigned to this plan.
                  {' '}Filter, select and queue them for building in the Pages workspace.
                </p>
              </div>
              <button
                onClick={() => navigate(`/pages?plan=${selectedPlan.id}&site=${selectedSite.id}`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                Open in Pages →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deploy Validation Modal */}
      {showDeployValidation && deployValidation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowDeployValidation(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[80vh] flex flex-col">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Before you deploy</h3>

            {deployValidation.error ? (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm mb-4">
                {deployValidation.error}
              </div>
            ) : (
              <>
                <div className="mb-4 grid grid-cols-3 gap-3 text-center">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded p-3">
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{deployValidation.ready_count}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Ready to Deploy</div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/30 rounded p-3">
                    <div className="text-2xl font-bold text-green-700 dark:text-green-300">{deployValidation.valid_count}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Will Deploy</div>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/30 rounded p-3">
                    <div className="text-2xl font-bold text-red-700 dark:text-red-300">{deployValidation.issues.length}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Will Fail</div>
                  </div>
                </div>

                {/* The yellow "no CONTENT-role repository" block was here. It
                    was wrong twice: `github_deployer.validate_config` asks for a
                    PRIMARY-role link, and it could never render — all three
                    branches that set `content_repo_configured` false also set
                    `error`, which the modal shows INSTEAD of this whole section.
                    It was also GitHub-specific, on a modal that now opens for
                    WordPress sites too. */}

                {/* Advisories deploy fine and cost something later, so they are
                    blue rather than yellow and sit BELOW the blocking warning:
                    a user skimming for the reason a deploy will fail should not
                    have to read past something that will not stop it. */}
                {(deployValidation.advisories || []).map((advisory, i) => (
                  <div
                    key={i}
                    className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 px-3 py-2 rounded text-sm mb-4 flex gap-2"
                  >
                    <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{advisory}</span>
                  </div>
                ))}

                {deployValidation.issues.length > 0 && (
                  <div className="flex-1 overflow-y-auto mb-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Issues ({deployValidation.issues.length})</p>
                    <div className="space-y-1">
                      {deployValidation.issues.map((issue) => (
                        <div key={issue.page_id} className="text-xs bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 rounded p-2">
                          <span className="font-medium text-red-800 dark:text-red-300">{issue.keyword_value || `Page ${issue.page_id}`}:</span>
                          <span className="text-red-600 dark:text-red-400 ml-1">{issue.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowDeployValidation(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
              >
                Cancel
              </button>
              {!deployValidation.error && deployValidation.valid_count > 0 && (
                <button
                  onClick={handleDeploy}
                  disabled={deploying}
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 text-sm"
                >
                  {deploying ? 'Starting...' : `Deploy ${deployValidation.valid_count} Page(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Plan Modal */}
      {showCreatePlanModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreatePlanModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">New deployment plan</h3>
            {/* The question this screen has never answered. Without it the
                obvious move is one plan per anything, and the page count on a
                plan stops meaning much. */}
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Most sites need only one. Add more when you want to publish
              different batches at different times.
            </p>
            {createPlanError && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded mb-4 text-sm">{createPlanError}</div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name *</label>
                <input
                  type="text"
                  value={createPlanForm.name || ''}
                  onChange={(e) => setCreatePlanForm({ ...createPlanForm, name: e.target.value })}
                  placeholder="Q1 2025 Content Launch"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                <textarea
                  value={createPlanForm.description || ''}
                  onChange={(e) => setCreatePlanForm({ ...createPlanForm, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowCreatePlanModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePlan}
                disabled={creatingPlan}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {creatingPlan ? 'Creating...' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
